import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Clock, FileText, ScrollText } from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { StatusBadge } from '../components/UI/StatusBadge';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import FormSelect from '../components/UI/FormSelect';
import ConfirmModal from '../components/UI/ConfirmModal';
import PdfViewer from '../components/UI/PdfViewer';
import { useAuth } from '../contexts/AuthContext';
import {
  useAcknowledgeResignation,
  useDisputeResignation,
  useResignationNotice,
  useTerminationCapabilities,
  useTerminationRequest,
  useTerminationRequests,
} from '../hooks/useTerminationRequests';
import { staffAPI } from '../services/api';
import { formatDate } from '../utils/dateUtils';

const WIZARD_STEPS = [
  { key: 'details', label: 'Details' },
  { key: 'notice', label: 'Notice' },
  { key: 'confirm', label: 'Confirm' },
];

const CIRCLE = {
  completed: 'bg-blue-800 text-white shadow-sm',
  current: 'bg-blue-800 text-white shadow-sm ring-4 ring-blue-200 animate-pulse',
  upcoming: 'bg-gray-200 text-gray-500',
};

const LABEL = {
  completed: 'text-blue-800 font-medium',
  current: 'text-blue-800 font-semibold',
  upcoming: 'text-gray-500',
};

const NoticeWizardSteps = ({ current, onSelect }) => {
  const currentIndex = WIZARD_STEPS.findIndex((step) => step.key === current);
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
        Resignation notice
      </h4>
      <div className="flex items-start">
        {WIZARD_STEPS.map((step, index) => {
          const state = index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming';
          const Icon = state === 'completed' ? Check : Clock;
          const canOpen = index <= currentIndex;
          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center min-w-0">
                <button
                  type="button"
                  disabled={!canOpen}
                  onClick={() => canOpen && onSelect(step.key)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${CIRCLE[state]} ${canOpen ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500' : 'cursor-default'}`}
                >
                  <Icon className="w-5 h-5" />
                </button>
                <span className={`text-xs mt-2 text-center max-w-[72px] leading-tight ${LABEL[state]}`}>
                  {step.label}
                </span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div className={`flex-1 h-1 rounded-full mx-2 mt-[18px] ${state === 'completed' ? 'bg-blue-800' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const SelfServiceResignationNotice = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const [step, setStep] = useState('details');
  const [language, setLanguage] = useState('');
  const [intentConfirmed, setIntentConfirmed] = useState(false);
  const [datesConfirmed, setDatesConfirmed] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [showDispute, setShowDispute] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');

  const { data: caps } = useTerminationCapabilities();
  const { data: openList = [] } = useTerminationRequests('open', {
    enabled: !selectedId && !!user,
  });
  const pendingNotices = useMemo(
    () => openList.filter((item) => item.status === 'awaiting_worker_acknowledgement'),
    [openList],
  );

  useEffect(() => {
    if (selectedId || pendingNotices.length !== 1) return;
    const next = new URLSearchParams(searchParams);
    next.set('id', pendingNotices[0].id);
    setSearchParams(next, { replace: true });
  }, [pendingNotices, selectedId, searchParams, setSearchParams]);

  const { data: detail, isLoading: detailLoading } = useTerminationRequest(selectedId);
  const { data: notice, isLoading: noticeLoading } = useResignationNotice(
    selectedId,
    language,
    { enabled: !!selectedId },
  );
  const acknowledge = useAcknowledgeResignation();
  const dispute = useDisputeResignation();

  useEffect(() => {
    if (!notice?.suggested_language || language) return;
    setLanguage(notice.suggested_language);
  }, [notice, language]);

  useEffect(() => {
    setIntentConfirmed(false);
    setDatesConfirmed(false);
  }, [notice?.content_hash]);

  useEffect(() => {
    let objectUrl = '';
    if (!selectedId || !detail?.acknowledgement) {
      setReceiptUrl('');
      return undefined;
    }
    staffAPI.downloadResignationReceiptPdf(selectedId)
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        setReceiptUrl(objectUrl);
      })
      .catch(() => setReceiptUrl(''));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedId, detail?.acknowledgement]);

  const languageOptions = (notice?.languages || []).map((item) => ({
    value: item.value,
    label: item.label,
  }));
  const canAck = (detail?.allowed_actions || []).includes('acknowledge');
  const canDispute = (detail?.allowed_actions || []).includes('dispute');
  const showWizard = canAck || canDispute;

  const submitAck = () => {
    acknowledge.mutate({
      id: selectedId,
      data: {
        language_code: notice.language_code,
        content_hash: notice.content_hash,
        intent_confirmed: intentConfirmed,
        dates_confirmed: datesConfirmed,
      },
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen max-w-3xl">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-5 h-5 text-blue-800" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Resignation notice</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Review the dates and confirm that you chose to leave. You do not draw a signature.
          </p>
        </div>

        {!selectedId && pendingNotices.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">
              {caps?.has_pending_resignation_ack
                ? 'Your notice is still loading'
                : 'You have no resignation notice to review'}
            </p>
            <Link to="/self-service" className="btn btn-outline mt-4">Back to Self-Service</Link>
          </div>
        )}

        {!selectedId && pendingNotices.length > 1 && (
          <div className="space-y-3">
            {pendingNotices.map((item) => (
              <button
                key={item.id}
                type="button"
                className="w-full text-left bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('id', item.id);
                  setSearchParams(next);
                }}
              >
                <p className="text-sm font-semibold text-gray-900">{item.request_number}</p>
                <p className="text-xs text-gray-500 mt-1 tabular-nums">
                  Proposed last day {formatDate(item.proposed_last_working_day)}
                </p>
              </button>
            ))}
          </div>
        )}

        {selectedId && (detailLoading || !detail) && (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        )}

        {detail && !showWizard && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{detail.request_number}</h2>
                <p className="text-sm text-gray-500">This notice is no longer waiting for your confirmation.</p>
              </div>
              <StatusBadge status={detail.status} />
            </div>
            {receiptUrl && (
              <PdfViewer
                src={receiptUrl}
                fileName={`${detail.request_number}-receipt.pdf`}
                title="Resignation notice receipt"
                theme="employee"
              />
            )}
          </div>
        )}

        {detail && showWizard && (
          <div className="space-y-4">
            <NoticeWizardSteps current={step} onSelect={setStep} />

            {step === 'details' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-gray-500">Worker</dt>
                    <dd className="font-medium text-gray-900">{detail.worker_name_snapshot}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Request</dt>
                    <dd className="font-medium text-gray-900">{detail.request_number}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Notice date</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.notice_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-gray-500">Proposed last working day</dt>
                    <dd className="font-medium text-gray-900 tabular-nums">{formatDate(detail.proposed_last_working_day)}</dd>
                  </div>
                </dl>
                <button type="button" className="btn btn-primary w-full" onClick={() => setStep('notice')}>
                  Continue to notice
                </button>
              </div>
            )}

            {step === 'notice' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                {noticeLoading || !notice ? (
                  <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
                ) : (
                  <>
                    <FormSelect
                      theme="employee"
                      options={languageOptions}
                      value={language || notice.language_code}
                      onChange={setLanguage}
                      placeholder="Language"
                    />
                    {notice.language_code !== 'en' && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 whitespace-pre-wrap text-sm text-gray-800">
                        {notice.translated_text}
                      </div>
                    )}
                    <div className="rounded-xl border border-gray-200 p-4 whitespace-pre-wrap text-sm text-gray-800">
                      {notice.english_text}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button type="button" className="btn btn-outline flex-1" onClick={() => setStep('details')}>
                        Back
                      </button>
                      <button type="button" className="btn btn-primary flex-1" onClick={() => setStep('confirm')}>
                        Continue to confirm
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 'confirm' && notice && (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={intentConfirmed}
                    onChange={(event) => setIntentConfirmed(event.target.checked)}
                  />
                  <span>{notice.confirm_intent}</span>
                </label>
                <label className="flex items-start gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={datesConfirmed}
                    onChange={(event) => setDatesConfirmed(event.target.checked)}
                  />
                  <span>{notice.confirm_dates}</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button type="button" className="btn btn-outline flex-1" onClick={() => setStep('notice')}>
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary flex-1"
                    disabled={!intentConfirmed || !datesConfirmed || acknowledge.isPending}
                    onClick={submitAck}
                  >
                    {notice.confirm_action}
                  </button>
                </div>
                {canDispute && (
                  <button
                    type="button"
                    className="btn btn-outline w-full text-red-700 border-red-200"
                    onClick={() => setShowDispute(true)}
                  >
                    {notice.dispute_action}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showDispute}
        theme="employee"
        variant="danger"
        title="Ask HR to review this notice"
        confirmLabel="Send to HR"
        confirmDisabled={!disputeReason.trim()}
        isLoading={dispute.isPending}
        onCancel={() => setShowDispute(false)}
        onConfirm={() => dispute.mutate(
          { id: selectedId, data: { reason: disputeReason } },
          { onSuccess: () => { setShowDispute(false); setDisputeReason(''); } },
        )}
      >
        <textarea
          className="input w-full"
          rows={3}
          value={disputeReason}
          onChange={(event) => setDisputeReason(event.target.value)}
          placeholder="What is wrong, or why you did not request this"
        />
      </ConfirmModal>
    </DashboardLayout>
  );
};

export default SelfServiceResignationNotice;
